import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Autocomplete,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  LinearProgress,
  FormControl,
  CircularProgress,
} from '@mui/material'
import SideBar from '../components/layout/SideBar'
import Footer from '../components/Footer'
import { ReactComponent as EditIcon } from '../assets/editIcon.svg'
import { ReactComponent as DeleteIcon } from '../assets/deleteIcon.svg'
import { ReactComponent as ListViewIcon } from '../assets/listViewIcon.svg'
import { ReactComponent as CardViewIcon } from '../assets/cardViewIcon.svg'
import { ReactComponent as ExportIcon } from '../assets/exportIcon.svg'
import { ReactComponent as AttachmentIcon } from '../assets/attachmentIcon.svg'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useSelector, useDispatch } from 'react-redux'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import Confirmation from '../components/Confirmation'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SearchIcon from '@mui/icons-material/Search'
import * as XLSX from 'xlsx'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import {
  fetchAssociates,
  createAssociate,
  deleteAssociate,
  getAssociate,
  updateAssociate,
  associatePasswordReset,
  checkEntityExist,
} from '../slices/associateApiSlice'
import { fetchSponsorDesignation } from '../slices/designationsApiSlice'
import { fetchData } from '../slices/userSlice'
import InputMask from 'react-input-mask'

const AssociateScreen = () => {
  const dispatch = useDispatch()
  const associates = useSelector((state) => state.associates.associates)

  const [open, setOpen] = useState(false)
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [associateToDelete, setAssociateToDelete] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [sortOrder, setSortOrder] = useState({ column: 'bidId', order: 'asc' })
  const [designationsData, setDesignationsData] = useState(null)
  const { list: designations } = useSelector((state) => state.designations)
  // eslint-disable-next-line
  const [action, setAction] = useState('save')
  // eslint-disable-next-line
  const [editAssociate, setEditAssociate] = useState(null)
  const [userRole, setUserRole] = useState('')
  // eslint-disable-next-line
  const [userName, setUserName] = useState('')
  const [selectedAssociate, setSelectedAssociate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingIds, setLoadingIds] = useState({})

  useEffect(() => {
    dispatch(fetchAssociates())
    dispatch(fetchData()).then((data) => {
      setUserRole(data.payload.data.userLoginDetailModel.role)
      setUserName(data.payload.data.userLoginDetailModel.userName)
    })
  }, [dispatch])

  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  // sort
  const filteredAssociates = useMemo(() => {
    const associatesCopy = [...associates]

    if (sortOrder.column === 'bidid') {
      associatesCopy.sort((a, b) => {
        const aId = parseInt(a.bidid.replace('BID', ''), 10)
        const bId = parseInt(b.bidid.replace('BID', ''), 10)

        if (sortOrder.order === 'asc') {
          if (aId < bId) return -1
          if (aId > bId) return 1
        } else {
          if (aId > bId) return -1
          if (aId < bId) return 1
        }
        return 0
      })
    } else if (sortOrder.column === 'designation') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.designation.localeCompare(b.designation)
          : b.designation.localeCompare(a.designation)
      )
    } else if (sortOrder.column === 'name') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      )
    } else if (sortOrder.column === 'dateofjoin') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.name.localeCompare(b.dateofjoin)
          : b.name.localeCompare(a.dateofjoin)
      )
    } else if (sortOrder.column === 'email') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.email.localeCompare(b.email)
          : b.email.localeCompare(a.email)
      )
    } else if (sortOrder.column === 'phoneNumber1') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.phone1.localeCompare(b.phone1)
          : b.phone1.localeCompare(a.phone1)
      )
    } else if (sortOrder.column === 'sponsoredBy') {
      associatesCopy.sort((a, b) =>
        sortOrder.order === 'asc'
          ? a.sponsoredBy.localeCompare(b.sponsoredBy)
          : b.sponsoredBy.localeCompare(a.sponsoredBy)
      )
    }

    return associatesCopy.filter((associate) =>
      [
        associate.bidid,
        associate.designation,
        associate.name,
        associate.email,
        associate.phoneNumber1,
        associate.sponsoredBy,
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  }, [associates, searchTerm, sortOrder])

  // Pagination of filtered data
  const totalPages = Math.ceil(filteredAssociates.length / rowsPerPage)
  const paginatedAssociates = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage
    return filteredAssociates.slice(startIndex, startIndex + rowsPerPage)
  }, [filteredAssociates, currentPage, rowsPerPage])

  const initialValues = {
    id: '',
    designation: '',
    designationid: '',
    name: '',
    fatherHusbandName: '',
    dateOfBirth: '',
    bankaccountnumber: '',
    occupation: '',
    weddingAnniversary: '',
    sponsoredBy: '',
    sponsoredById: '',
    email: '',
    phone1: '',
    phone2: '',
    address: '',
    pan: '',
    aadhar: '',
    dateOfJoin: new Date().toDateString(),
    assContactId: '',
    assIdentityId: '',
  }

  // Formik
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      designation: Yup.string().required('Designation is required'),
      name: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Name can only contain letters')
        .required('Name is required'),
      fatherHusbandName: Yup.string()
        .matches(
          /^[A-Za-z\s]+$/,
          'Father/Husband Name can only contain letters'
        )
        .nullable(),
      dateOfBirth: Yup.date().nullable(),
      occupation: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Occupation can only contain letters')
        .nullable(),
      weddingAnniversary: Yup.date().nullable(),
      dateOfJoin: Yup.date().nullable(),
      bankaccountnumber: Yup.string()
        //.min(18,'Bank account number must be between 18 digits')
        .matches(/^\d{18}$/, 'Bank account number must be 18 digits'),
      sponsoredBy: Yup.string().required('Sponsored By is required'),
      email: Yup.string().email('Invalid email address').nullable(),
      phone1: Yup.string()
        .required('Phone Number 1 is required')

        .min(10, 'Phone Number should be 10 charater length')
        .matches(/^[0-9+\s]+$/, 'Phone Number can only contain numbers')
        .test('unique-phone', 'Phone number already in use', async (value) => {
          if (!value || value.length < 10) return true
          return !(await handleEntityExist('mobile', value))
        }),
      phone2: Yup.string()
        .matches(/^[0-9+\s]+$/, 'Phone Number 2 can only contain numbers')
        .nullable(),
      address: Yup.string().required('Address is required'),
      pan: Yup.string()
        .nullable()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Invalid PAN Card format')
        .test('unique-pan', 'PAN already in use', async (value) => {
          if (!value) return true
          return value.length === 10 && !(await handleEntityExist('pan', value))
        }),

      aadhar: Yup.string()
        .min(12, 'AADHAR should be 12 charater length')
        .required('Aadhar is required')
        .matches(/^[0-9]+$/, 'Aadhar can only contain numbers')
        .test('unique-aadhar', 'AADHAR already in use', async (value) => {
          if (!value || value.length < 12) return true
          return !(await handleEntityExist('adhaar', value))
        }),
    }),

    onSubmit: (values) => {
      if (values.residentialProof) {
        localStorage.setItem(
          'associate_residentialProof',
          values.residentialProof.base64
        )
      }
      if (values.panCard) {
        localStorage.setItem('associate_panCard', values.panCard.base64)
      }
      if (values.aadharCard) {
        localStorage.setItem('associate_aadharCard', values.aadharCard.base64)
      }
      if (openAddDialog) {
        dispatch(createAssociate(values))
          .then(() => {
            setSnackbarMessage('New associate added successfully')
            setSnackbarSeverity('success')
          })
          .catch(() => setSnackbarMessage('Failed to save associate', 'error'))
      } else {
        dispatch(updateAssociate(values))
          .then(() => {
            setSnackbarMessage('Associate Details Updated successfully')
            setSnackbarSeverity('info')
          })
          .catch(() =>
            setSnackbarMessage('Failed to update associate', 'error')
          )
      }
      setSnackbarOpen(true)
      handleClose()
    },
  })

  const handleDelete = (_id) => {
    dispatch(deleteAssociate(_id))
      .then(() => {
        setSnackbarMessage('Associate Deleted successfully', 'success')
        dispatch(fetchAssociates())
      })
      .catch(() => setSnackbarMessage('Failed to delete associate', 'error'))
  }

  const handleDeleteClick = (associate) => {
    setAssociateToDelete(associate)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (associateToDelete) {
      handleDelete(associateToDelete.id)
      setSnackbarMessage('Associate deleted successfully')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
    setIsDeleteDialogOpen(false)
  }

  const handlePasswordReset = (selAssociate) => {
    const passwordRestModel = {
      associateId: selAssociate.id,
      phoneNumber: selAssociate.phone1,
    }
    dispatch(associatePasswordReset(passwordRestModel))
      .then((data) => {
        setSnackbarMessage('Password reset successfully.')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
      })
      .catch((error) => {
        console.error('Password reset failed:', error)
        setSnackbarMessage('Failed to reset password.')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      })
  }

  const handleEdit = (associate) => {
    setLoadingIds((prev) => ({ ...prev, [associate.id]: true }))
    setLoading(true)
    dispatch(getAssociate(associate.id)).then((data) => {
      const associateDetails = data.payload.data
      setSelectedAssociate(associateDetails)

      setEditAssociate(associateDetails)

      dispatch(
        fetchSponsorDesignation(
          associateDetails.associateBasicDetailsModel.sponsoredByIDName.split(
            ':'
          )[0]
        )
      ).then((result) => {
        setDesignationsData(result.payload.data)

        setOpen(true)
        setLoadingIds((prev) => ({ ...prev, [associate.id]: false }))
      })
      setAction('edit')
    })

    setDesignationsData(designations)
  }

  const handleEntityExist = async (entity, value) => {
    const inputs = {
      entity: entity,
      value: value,
      userid:
        editAssociate === null ||
        editAssociate.associateBasicDetailsModel === null
          ? 0
          : editAssociate.associateBasicDetailsModel.id,
    }
    const data = await dispatch(checkEntityExist(inputs))
    return data.payload.data.status
  }

  useEffect(() => {
    if (selectedAssociate) {
      formik.setValues({
        designation:
          selectedAssociate?.associateBasicDetailsModel?.designationCodeName ||
          '',
        id: selectedAssociate?.associateBasicDetailsModel?.id || '',
        designationid:
          selectedAssociate?.associateBasicDetailsModel?.designationId || '',
        name: selectedAssociate?.associateBasicDetailsModel?.name || '',
        fatherHusbandName:
          selectedAssociate?.associateBasicDetailsModel?.fatherHusbandName ||
          '',
        dateOfBirth:
          selectedAssociate?.associateBasicDetailsModel?.dateOfBirth || '',
        bankaccountnumber:
          selectedAssociate?.associateBasicDetailsModel?.bankAccountNumber ||
          '',
        occupation:
          selectedAssociate?.associateBasicDetailsModel?.occupation || '',
        weddingAnniversary:
          selectedAssociate?.associateBasicDetailsModel?.weddingAnniversary ||
          '',
        sponsoredBy:
          selectedAssociate?.associateBasicDetailsModel?.sponsoredByIDName ||
          '',
        sponsoredById:
          selectedAssociate?.associateBasicDetailsModel?.sponsoredBy || '',
        email: selectedAssociate?.associateContactDetailsModel?.email || '',
        phone1:
          selectedAssociate?.associateContactDetailsModel?.phoneNumber1 || '',
        phone2:
          selectedAssociate?.associateContactDetailsModel?.phoneNumber2 || '',
        address: selectedAssociate?.associateContactDetailsModel?.address || '',
        pan: selectedAssociate?.associateIdentityDetailsModel?.panNumber || '',
        aadhar:
          selectedAssociate?.associateIdentityDetailsModel?.aadhaarNumber || '',
        dateOfJoin:
          selectedAssociate?.associateBasicDetailsModel?.dateOfJoin || '',
        assContactId: selectedAssociate?.associateContactDetailsModel?.id || '',
        assIdentityId:
          selectedAssociate?.associateIdentityDetailsModel?.id || '',
      })
      setLoading(false)
      setOpen(true)
    }
    // eslint-disable-next-line
  }, [selectedAssociate])

  // const residentialProof = localStorage.getItem('associate_residentialProof')
  // const panCard = localStorage.getItem('associate_panCard')
  // const aadharCard = localStorage.getItem('associate_aadharCard')

  // if (residentialProof)
  //   formik.setFieldValue('residentialProof', {
  //     name: associate?.residentialProof?.name,
  //     base64: residentialProof,
  //   })
  // if (panCard)
  //   formik.setFieldValue('panCard', {
  //     name: associate?.panCard?.name,
  //     base64: panCard,
  //   })
  // if (aadharCard)
  //   formik.setFieldValue('aadharCard', {
  //     name: associate?.aadharCard?.name,
  //     base64: aadharCard,
  //   })

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleSkipToFirst = () => {
    setCurrentPage(1)
  }

  const handleSkipToLast = () => {
    setCurrentPage(totalPages)
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value)
    setCurrentPage(1)
  }
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleClose = () => {
    formik.resetForm()
    setOpen(false)
    setOpenAddDialog(false)
    setSelectedAssociate(null)
  }

  const handleAddNew = () => {
    formik.resetForm()
    setOpenAddDialog(true)
    setDesignationsData(designations)
    setAction('save')
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleSort = (column) => {
    setSortOrder((prev) => ({
      column,
      order: prev.column === column && prev.order === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleFileUpload = async (event, fileType) => {
    const file = event.target.files[0]
    if (file) {
      try {
        const base64File = await convertFileToBase64(file)

        formik.setFieldValue(fileType, { name: file.name, base64: base64File })

        localStorage.setItem(`associate_${fileType}`, base64File)
      } catch (error) {
        console.error('Error converting file:', error)
      }
    }
  }

  const handleExport = () => {
    const exportData = associates.map(({ _id, ...rest }) => rest)

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Associates')
    XLSX.writeFile(workbook, 'Associates_Data.xlsx')
  }

  return (
    <>
      <Box sx={{ display: 'flex', height: '98vh' }}>
        <SideBar />
        {/* parent */}
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            marginBottom: 2,
            marginRight: 2,
            marginTop: 9,
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: 2,
          }}>
          {/* first Box */}

          <Box
            sx={{
              width: '100%',
              height: '100%',
              padding: 1,
              background: '#F6FAFF',
              borderRadius: '20px',
              overflow: 'hidden',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              display: 'flex',
            }}>
            {/* associate Box */}
            <Box
              sx={{
                width: '100%',
                padding: 0,
                backgroundColor: '#F6FAFF',
                borderRadius: 2,
                display: 'flex',
              }}>
              <Box
                sx={{
                  width: '100%',
                  padding: 0,
                  background: '#F6FAFF',
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                <Typography
                  sx={{
                    color: '#F6764E',
                    fontSize: 20,
                    fontWeight: 600,
                    wordWrap: 'break-word',
                    marginRight: '24px',
                  }}>
                  Associates
                </Typography>
                {userRole === 'Associate' ? (
                  ''
                ) : (
                  <Button
                    variant='contained'
                    onClick={handleAddNew}
                    sx={{
                      backgroundColor: '#F5774D',
                      color: '#FFFFFF',
                      borderRadius: '20px',
                      textTransform: 'none',
                      px: 3,
                      '&:hover': {
                        backgroundColor: '#FF5C33',
                      },
                    }}>
                    Add New
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  padding: 0,
                  background: '#F6FAFF',
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <IconButton size='small'>
                  <CardViewIcon style={{ opacity: 0.5 }} />
                </IconButton>
                <IconButton size='small'>
                  <ListViewIcon />
                </IconButton>
              </Box>
            </Box>
            {/* record/page box */}
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#F6FAFF',
                display: 'flex',
              }}>
              <Box
                sx={{
                  width: '100%',
                  background: '#F6FAFF',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                <Typography
                  sx={{
                    color: '#081332',
                    fontSize: 18,
                    fontWeight: 500,
                    wordWrap: 'break-word',
                    marginRight: '11px',
                    fontFamily: 'Work Sans',
                  }}>
                  Records Per Page:
                </Typography>
                <Box>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    displayEmpty
                    IconComponent={ArrowDropDownIcon}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '2px',
                      px: 2,
                      py: 0,
                      height: '36px',
                      boxShadow: 3,
                    }}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                </Box>
                <IconButton onClick={handleExport}>
                  <ExportIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: 3,
                  borderRadius: '20px',
                  backgroundColor: 'white',
                  height: '36px',
                  width: '520px',
                }}>
                <SearchIcon sx={{ fontSize: '20px', ml: 2 }} />
                <TextField
                  variant='standard'
                  placeholder='Search...'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      style: {
                        padding: 0,
                      },
                    },
                  }}
                  sx={{ ml: 1, height: '20px', width: '100%' }}
                />
              </Box>
            </Box>
            {/* main table Box */}
            <Box
              sx={{
                width: '100%',
                padding: 0,
                margin: 0,
                background: '#F6FAFF',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                height: '60vh',
              }}>
              <TableContainer
                sx={{
                  maxHeight: '60vh',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#AAB4C6',
                    borderRadius: '10px',
                    border: '2px solid #f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#555',
                  },
                }}>
                <Table
                  stickyHeader
                  sx={{
                    minWidth: 650,
                    backgroundColor: '#F6FAFF',
                  }}
                  aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box
                          display='flex'
                          alignItems='center'
                          boxSizing='content-box'>
                          BID-ID
                          <IconButton
                            size='small'
                            sx={{
                              marginLeft: '10px',
                              marginRight: '0',
                            }}
                            onClick={() => handleSort('bidId')}>
                            {sortOrder.column === 'bidId' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'bidId'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'bidId'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Designation
                          <IconButton
                            size='small'
                            sx={{ marginLeft: '10px' }}
                            onClick={() => handleSort('designation')}>
                            {sortOrder.column === 'designation' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'designation'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'designation'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Name
                          <IconButton
                            size='small'
                            sx={{ marginLeft: '10px' }}
                            onClick={() => handleSort('name')}>
                            {sortOrder.column === 'name' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'name'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'name'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Date Of Join
                          <IconButton
                            size='small'
                            sx={{ marginLeft: '10px' }}
                            onClick={() => handleSort('dateofjoin')}>
                            {sortOrder.column === 'dateofjoin' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'dateofjoin'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'dateofjoin'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Email
                          <IconButton
                            size='small'
                            onClick={() => handleSort('email')}>
                            {sortOrder.column === 'email' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'email'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'email'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Phone
                          <IconButton
                            size='small'
                            onClick={() => handleSort('phone1')}>
                            {sortOrder.column === 'phone1' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'phone1'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'phone1'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          paddingRight: '0',
                          backgroundColor: '#F6FAFF',
                        }}>
                        <Box display='flex' alignItems='center'>
                          Sponsored By
                          <IconButton
                            size='small'
                            onClick={() => handleSort('sponsoredBy')}>
                            {sortOrder.column === 'sponsoredBy' &&
                            sortOrder.order === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'sponsoredBy'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{
                                  color:
                                    sortOrder.column === 'sponsoredBy'
                                      ? '#F6764E'
                                      : '#FBBAB1',
                                }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          backgroundColor: '#F6FAFF',
                        }}></TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'Work Sans',
                          color: '#081332',
                          fontSize: 18,
                          fontWeight: 500,
                          borderBottom: 'none',
                          paddingTop: '0px',
                          paddingBottom: '16px',
                          backgroundColor: '#F6FAFF',
                        }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAssociates.map((associate) => (
                      <TableRow
                        key={associate.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}>
                        <TableCell
                          component='th'
                          scope='row'
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.bidid}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.designation}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.dateOfJoin}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.email}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {` ${associate.phoneNumber1}`}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {associate.sponsoredBy}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                          }}>
                          {userRole === 'Associate' ? (
                            ''
                          ) : loadingIds[associate.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <IconButton
                              size='small'
                              onClick={() => {
                                setLoading(true)
                                handleEdit(associate)
                              }}>
                              <EditIcon />
                            </IconButton>
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            borderBottom: 'none',
                            paddingTop: '0px',
                            paddingBottom: '12px',
                            border: 'none',
                          }}>
                          {userRole === 'Associate' ? (
                            ''
                          ) : (
                            <IconButton
                              size='small'
                              onClick={() => handleDeleteClick(associate)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Edit Dialog */}
                {open && !loading && (
                  <Dialog
                    open={open}
                    onClose={handleClose}
                    fullWidth
                    maxWidth='md'
                    sx={{
                      '& .MuiPaper-root': {
                        backgroundColor: '#F6FAFF',
                      },
                    }}>
                    <DialogTitle>
                      <Typography
                        variant='h6'
                        component='span'
                        color='primary'
                        sx={{
                          fontFamily: 'Work Sans',
                          fontSize: '22px',
                          fontWeight: 600,
                          lineHeight: '28.15px',
                        }}>
                        Edit
                      </Typography>{' '}
                      <Typography
                        component='span'
                        variant='h6'
                        color='textPrimary'
                        sx={{
                          color: 'black',
                          fontFamily: 'Work Sans',
                          fontSize: '22px',
                          fontWeight: 600,
                          lineHeight: '28.15px',
                        }}>
                        Associate (BID{formik.values.id}: {formik.values.name})
                      </Typography>
                      <IconButton
                        onClick={handleClose}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          color: '#49454F',
                        }}>
                        <HighlightOffIcon
                          sx={{
                            fontSize: 30,
                          }}
                        />
                      </IconButton>
                    </DialogTitle>

                    <DialogContent>
                      <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                          {/* Basic Details Section */}
                          <Grid item xs={12}>
                            <Typography
                              variant='subtitle1'
                              color='#081332'
                              sx={{
                                fontFamily: 'Work Sans',
                                fontSize: '18px',
                                fontWeight: 500,
                                lineHeight: '23.46px',
                              }}>
                              Basic Details
                            </Typography>
                          </Grid>
                          {/* edit associate - sponsored by  */}

                          <Grid item xs={4}>
                            <FormControl
                              variant='filled'
                              fullWidth
                              error={
                                formik.touched.sponsoredBy &&
                                Boolean(formik.errors.sponsoredBy)
                              }
                              sx={{ mb: 1 }}>
                              <Autocomplete
                                disablePortal
                                options={associates}
                                getOptionLabel={(option) =>
                                  `${option.bidid}: ${option.name}`
                                }
                                value={
                                  associates.find(
                                    (associate) =>
                                      `${associate.bidid}:${associate.name}` ===
                                      formik.values.sponsoredBy
                                  ) || null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    const { bidid, name, id } = newValue
                                    const selectedCode = bidid

                                    formik.setValues((prevValues) => ({
                                      ...prevValues,
                                      designation: '',
                                      sponsoredBy: `${bidid}:${name}`,
                                      sponsoredById: id,
                                    }))

                                    if (
                                      selectedCode !==
                                      formik.values.sponsoredById
                                    ) {
                                      dispatch(
                                        fetchSponsorDesignation(selectedCode)
                                      )
                                        .then((result) => {
                                          setDesignationsData(
                                            result.payload.data
                                          )
                                        })
                                        .catch((error) =>
                                          console.error(
                                            'Error fetching sponsor designation:',
                                            error
                                          )
                                        )
                                    }
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Sponsored By*'
                                    variant='filled'
                                    name='sponsoredBy'
                                    error={
                                      formik.touched.sponsoredBy &&
                                      Boolean(formik.errors.sponsoredBy)
                                    }
                                    helperText={
                                      formik.touched.sponsoredBy &&
                                      formik.errors.sponsoredBy
                                    }
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      '& .MuiFilledInput-root': {
                                        backgroundColor: '#FFFFFF',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          {/* edit dialog - designation field  */}

                          <Grid item xs={4}>
                            <FormControl
                              variant='filled'
                              fullWidth
                              error={
                                formik.touched.designation &&
                                Boolean(formik.errors.designation)
                              }
                              sx={{ mb: 1 }}>
                              <Autocomplete
                                disablePortal
                                options={designationsData}
                                getOptionLabel={(option) =>
                                  `${option.code} - ${option.name}`
                                }
                                value={
                                  designationsData.find(
                                    (designation) =>
                                      `${designation.code}-${designation.name}` ===
                                      formik.values.designation
                                  ) || null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    const { code, name, id } = newValue
                                    formik.setFieldValue(
                                      'designation',
                                      `${code}-${name}`
                                    )
                                    formik.setFieldValue('designationid', id)
                                  } else {
                                    formik.setFieldValue('designation', '')
                                    formik.setFieldValue('designationid', '')
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Designation*'
                                    variant='filled'
                                    name='designation'
                                    error={
                                      formik.touched.designation &&
                                      Boolean(formik.errors.designation)
                                    }
                                    helperText={
                                      formik.touched.designation &&
                                      formik.errors.designation
                                    }
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      '& .MuiFilledInput-root': {
                                        backgroundColor: '#FFFFFF',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          {/* edit dialog - name field  */}
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Name'
                              name='name'
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.name &&
                                Boolean(formik.errors.name)
                              }
                              helperText={
                                formik.touched.name && formik.errors.name
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('name', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label="Father/Husband's Name"
                              name='fatherHusbandName'
                              value={formik.values.fatherHusbandName}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.fatherHusbandName &&
                                Boolean(formik.errors.fatherHusbandName)
                              }
                              helperText={
                                formik.touched.fatherHusbandName &&
                                formik.errors.fatherHusbandName
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue(
                                        'fatherHusbandName',
                                        ''
                                      )
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Occupation'
                              name='occupation'
                              value={formik.values.occupation}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.occupation &&
                                Boolean(formik.errors.occupation)
                              }
                              helperText={
                                formik.touched.occupation &&
                                formik.errors.occupation
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('occupation', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          {/* edit */}
                          <Grid item xs={4}>
                            <DatePicker
                              label='Date of Birth'
                              inputFormat='MM/DD/YYYY'
                              value={
                                formik.values.dateOfBirth
                                  ? dayjs(formik.values.dateOfBirth)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'dateOfBirth',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant='filled'
                                  error={
                                    formik.touched.dateOfBirth &&
                                    Boolean(formik.errors.dateOfBirth)
                                  }
                                  helperText={
                                    formik.touched.dateOfBirth &&
                                    formik.errors.dateOfBirth
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'dateOfBirth',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={4}>
                            <DatePicker
                              label='Wedding Anniversary'
                              inputFormat='MM/DD/YYYY'
                              value={
                                formik.values.weddingAnniversary
                                  ? dayjs(formik.values.weddingAnniversary)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'weddingAnniversary',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant='filled'
                                  error={
                                    formik.touched.weddingAnniversary &&
                                    Boolean(formik.errors.weddingAnniversary)
                                  }
                                  helperText={
                                    formik.touched.weddingAnniversary &&
                                    formik.errors.weddingAnniversary
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'weddingAnniversary',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>

                          {/* edit - date of join  */}
                          <Grid item xs={4}>
                            <DatePicker
                              label='Date of Join'
                              inputFormat='MM/DD/YYYY'
                              value={
                                formik.values.dateOfJoin
                                  ? dayjs(formik.values.dateOfJoin)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'dateOfJoin',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant='filled'
                                  error={
                                    formik.touched.dateOfJoin &&
                                    Boolean(formik.errors.dateOfJoin)
                                  }
                                  helperText={
                                    formik.touched.dateOfJoin &&
                                    formik.errors.dateOfJoin
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue('dateOfJoin', '')
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>
                          {/* Bank Account Number */}
                          <Grid item xs={4}>
                            <InputMask
                              mask='999999999999999999' // 9-digit mask for the bank account number
                              value={formik.values.bankaccountnumber}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              alwaysShowMask={false}>
                              {(inputProps) => (
                                <TextField
                                  variant='filled'
                                  label='Bank Account Number'
                                  name='bankaccountnumber'
                                  value={formik.values.bankaccountnumber}
                                  onChange={inputProps.handleChange}
                                  onBlur={inputProps.handleBlur}
                                  error={
                                    formik.touched.bankaccountnumber &&
                                    Boolean(formik.errors.bankaccountnumber)
                                  }
                                  helperText={
                                    formik.touched.bankaccountnumber &&
                                    formik.errors.bankaccountnumber
                                  }
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'bankaccountnumber',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 1 }}
                                />
                              )}
                            </InputMask>
                          </Grid>

                          {/* Contact Details Section */}
                          <Grid item xs={12}>
                            <Typography
                              variant='subtitle1'
                              color='#081332'
                              sx={{
                                fontFamily: 'Work Sans',
                                fontSize: '18px',
                                fontWeight: 500,
                                lineHeight: '23.46px',
                              }}>
                              Contact Details
                            </Typography>
                          </Grid>

                          {/* Email Field */}
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Email'
                              name='email'
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                              }
                              helperText={
                                formik.touched.email && formik.errors.email
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('email', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>

                          {/* Phone Number 1 Field */}
                          <Grid item xs={4}>
                            <InputMask
                              mask='9999999999' // 9-digit mask for the bank account number
                              value={formik.values.phone1}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              alwaysShowMask={false}>
                              {(inputProps) => (
                                <TextField
                                  variant='filled'
                                  label='Phone Number 1'
                                  name='phone1'
                                  value={formik.values.phone1}
                                  onChange={inputProps.handleChange}
                                  onBlur={inputProps.handleBlur}
                                  error={
                                    formik.touched.phone1 &&
                                    Boolean(formik.errors.phone1)
                                  }
                                  helperText={
                                    formik.touched.phone1 &&
                                    formik.errors.phone1
                                  }
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue('phone1', '')
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 1 }}
                                />
                              )}
                            </InputMask>
                          </Grid>

                          {/* Address for Communication Field */}
                          <Grid item xs={4}>
                            <Box sx={{ position: 'relative' }}>
                              {' '}
                              <TextField
                                variant='filled'
                                label='Address for Communication'
                                name='address'
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.address &&
                                  Boolean(formik.errors.address)
                                }
                                helperText={
                                  formik.touched.address &&
                                  formik.errors.address
                                }
                                fullWidth
                                multiline
                                rows={5}
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() =>
                                        formik.setFieldValue('address', '')
                                      }>
                                      <HighlightOffIcon
                                        sx={{ color: '#49454F' }}
                                      />
                                    </IconButton>
                                  ),
                                  sx: { backgroundColor: '#FFFFFF' },
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 1 }}
                              />
                            </Box>
                          </Grid>

                          {/* Phone Number 2 Field */}
                          <Grid
                            item
                            xs={4}
                            sx={{
                              position: 'relative',
                              top: '-5rem',
                            }}>
                            <TextField
                              variant='filled'
                              label='Phone Number 2'
                              name='phone2'
                              value={formik.values.phone2}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.phone2 &&
                                Boolean(formik.errors.phone2)
                              }
                              helperText={
                                formik.touched.phone2 && formik.errors.phone2
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('phone2', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>

                          {/* Identity Details Section */}
                          <Grid container spacing={2}>
                            <Grid
                              item
                              xs={12}
                              sx={{ marginLeft: '20px', marginTop: '-2rem' }}>
                              <Typography
                                variant='subtitle1'
                                color='#081332'
                                sx={{
                                  fontFamily: 'Work Sans',
                                  fontSize: '18px',
                                  fontWeight: 500,
                                  lineHeight: '23.46px',
                                }}>
                                Identity Details
                              </Typography>
                            </Grid>

                            <Grid item xs={7.2} sx={{ marginLeft: '20px' }}>
                              <Grid container spacing={2}>
                                {/* pan number details - edit */}
                                <Grid item xs={5.5}>
                                  <TextField
                                    variant='filled'
                                    label='PAN Number'
                                    name='pan'
                                    value={formik.values.pan.toUpperCase()}
                                    onChange={(e) => {
                                      let value = e.target.value.toUpperCase()
                                      value = value
                                        .slice(0, 10)
                                        .split('')
                                        .map((char, index) => {
                                          if (index < 5)
                                            return char.replace(/[^A-Z]/g, '')
                                          if (index >= 5 && index < 9)
                                            return char.replace(/[^0-9]/g, '')
                                          if (index === 9)
                                            return char.replace(/[^A-Z]/g, '')
                                          return ''
                                        })
                                        .join('')

                                      formik.setFieldValue('pan', value)
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={
                                      formik.touched.pan &&
                                      Boolean(formik.errors.pan)
                                    }
                                    helperText={
                                      formik.touched.pan && formik.errors.pan
                                    }
                                    InputProps={{
                                      endAdornment: (
                                        <IconButton
                                          onClick={() =>
                                            formik.setFieldValue('pan', '')
                                          }>
                                          <HighlightOffIcon
                                            sx={{ color: '#49454F' }}
                                          />
                                        </IconButton>
                                      ),
                                      sx: { backgroundColor: '#FFFFFF' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: '99%' }}
                                    inputProps={{
                                      maxLength: 10,
                                      style: { textTransform: 'uppercase' },
                                    }}
                                  />
                                </Grid>

                                {/* aadhar card  */}
                                <Grid item xs={5.5}>
                                  <InputMask
                                    mask='999999999999'
                                    value={formik.values.aadhar}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    alwaysShowMask={false}
                                    disabled={Boolean(
                                      userRole !== 'Super Admin'
                                    )}>
                                    {(inputProps) => (
                                      <TextField
                                        variant='filled'
                                        label='AADHAAR Number'
                                        name='aadhar'
                                        value={formik.values.aadhar}
                                        onChange={inputProps.handleChange}
                                        onBlur={inputProps.handleBlur}
                                        error={
                                          formik.touched.aadhar &&
                                          Boolean(formik.errors.aadhar)
                                        }
                                        helperText={
                                          formik.touched.aadhar &&
                                          formik.errors.aadhar
                                        }
                                        InputProps={{
                                          endAdornment: (
                                            <IconButton
                                              onClick={() =>
                                                formik.setFieldValue(
                                                  'aadhar',
                                                  ''
                                                )
                                              }
                                              disabled={true}>
                                              <HighlightOffIcon
                                                sx={{ color: '#49454F' }}
                                              />
                                            </IconButton>
                                          ),
                                          sx: { backgroundColor: '#FFFFFF' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: '92%' }}
                                      />
                                    )}
                                  </InputMask>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* Edit assaciote - Enclosures Section */}
                            <Grid
                              item
                              xs={4.4}
                              sx={{
                                position: 'relative',
                                top: '-2rem',
                                left: '-1rem',
                              }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}>
                                <Typography
                                  variant='subtitle1'
                                  color='#081332'
                                  sx={{
                                    fontFamily: 'Work Sans',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    lineHeight: '23.46px',
                                  }}>
                                  Enclosures
                                </Typography>

                                {/* Residential Proof */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    Residential Proof
                                  </Typography>
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById(
                                          'residentialProofUpload'
                                        )
                                        .click()
                                    }>
                                    <AttachmentIcon
                                      sx={{
                                        fontSize: 16,
                                      }}
                                    />
                                  </IconButton>
                                  {formik.values.residentialProof && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '4vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.residentialProof.name}
                                    </Typography>
                                  )}

                                  <input
                                    type='file'
                                    id='residentialProofUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'residentialProof')
                                    }
                                  />
                                </Box>

                                {/* PAN Card */}

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    PAN Card
                                  </Typography>

                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById('panCardUpload')
                                        .click()
                                    }>
                                    <AttachmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {formik.values.panCard && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '7.4vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.panCard.name}
                                    </Typography>
                                  )}
                                  <input
                                    type='file'
                                    id='panCardUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'panCard')
                                    }
                                  />
                                </Box>

                                {/* Aadhaar Card */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    Aadhaar Card
                                  </Typography>

                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById('aadharCardUpload')
                                        .click()
                                    }>
                                    <AttachmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {formik.values.aadharCard && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '5.6vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.aadharCard.name}
                                    </Typography>
                                  )}
                                  <input
                                    type='file'
                                    id='aadharCardUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'aadharCard')
                                    }
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <DialogActions
                          sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            sx={{
                              borderRadius: '20px',
                              width: '112px',
                              color: 'white',
                            }}>
                            Save
                          </Button>
                          <Button
                            onClick={handleClose}
                            variant='outlined'
                            color='secondary'
                            sx={{
                              backgroundColor: '#C5C5C5',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: '#A5A5A5',
                              },
                              ml: 2,
                            }}>
                            Cancel
                          </Button>
                          <Button
                            onClick={() => {
                              handlePasswordReset(formik.values)
                            }}
                            variant='outlined'
                            color='secondary'
                            sx={{
                              backgroundColor: '#C5C5C5',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: '#A5A5A5',
                              },
                              ml: 2,
                            }}>
                            Reset Password
                          </Button>
                        </DialogActions>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Add associates dialog  */}
                {openAddDialog && (
                  <Dialog
                    open={openAddDialog}
                    onClose={handleClose}
                    fullWidth
                    maxWidth='md'
                    sx={{ '& .MuiPaper-root': { backgroundColor: '#F6FAFF' } }}>
                    <DialogTitle>
                      <Typography
                        component='span'
                        variant='h6'
                        color='primary'
                        sx={{
                          fontFamily: 'Work Sans',
                          fontSize: '22px',
                          fontWeight: 600,
                          lineHeight: '28.15px',
                        }}>
                        Add
                      </Typography>{' '}
                      <Typography
                        component='span'
                        variant='h6'
                        color='textPrimary'
                        sx={{
                          color: 'black',
                          fontFamily: 'Work Sans',
                          fontSize: '22px',
                          fontWeight: 600,
                          lineHeight: '28.15px',
                        }}>
                        Associate
                      </Typography>
                      <IconButton
                        onClick={handleClose}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          color: '#49454F',
                        }}>
                        <HighlightOffIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </DialogTitle>

                    <DialogContent>
                      <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                          {/* Basic Details Section */}
                          <Grid item xs={12}>
                            <Typography
                              variant='subtitle1'
                              color='#081332'
                              sx={{
                                fontFamily: 'Work Sans',
                                fontSize: '18px',
                                fontWeight: 500,
                                lineHeight: '23.46px',
                              }}>
                              Basic Details
                            </Typography>
                          </Grid>

                          {/* add associate - Sponsored By autocomplete  */}
                          <Grid item xs={4}>
                            <FormControl
                              variant='filled'
                              fullWidth
                              error={
                                formik.touched.sponsoredBy &&
                                Boolean(formik.errors.sponsoredBy)
                              }
                              sx={{ mb: 1, backgroundColor: '#FFFFFF' }}>
                              <Autocomplete
                                disablePortal
                                options={associates}
                                getOptionLabel={(option) =>
                                  `${option.bidid}: ${option.name}`
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    const { bidid, name, id } = newValue
                                    const selectedCode = bidid
                                    formik.setFieldValue('designation', '')

                                    dispatch(
                                      fetchSponsorDesignation(selectedCode)
                                    ).then((result) => {
                                      setDesignationsData(result.payload.data)
                                    })

                                    formik.setFieldValue(
                                      'sponsoredBy',
                                      `${bidid}:${name}`
                                    )
                                    formik.setFieldValue('sponsoredById', id)
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Sponsored By*'
                                    variant='filled'
                                    name='sponsoredBy'
                                    error={
                                      formik.touched.sponsoredBy &&
                                      Boolean(formik.errors.sponsoredBy)
                                    }
                                    helperText={
                                      formik.touched.sponsoredBy &&
                                      formik.errors.sponsoredBy
                                    }
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      '& .MuiFilledInput-root': {
                                        backgroundColor: '#FFFFFF',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>
                          {/* add associate - Designation autocomplete  */}

                          <Grid item xs={4}>
                            <FormControl
                              variant='filled'
                              fullWidth
                              error={
                                formik.touched.designation &&
                                Boolean(formik.errors.designation)
                              }
                              sx={{ mb: 1, backgroundColor: '#FFFFFF' }}>
                              <Autocomplete
                                disablePortal
                                options={designationsData}
                                getOptionLabel={(option) =>
                                  `${option.code}-${option.name}`
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    const { code, name, id } = newValue
                                    formik.setFieldValue(
                                      'designation',
                                      `${code}-${name}`
                                    )
                                    formik.setFieldValue('designationid', id)
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Designation*'
                                    variant='filled'
                                    name='designation'
                                    error={
                                      formik.touched.designation &&
                                      Boolean(formik.errors.designation)
                                    }
                                    helperText={
                                      formik.touched.designation &&
                                      formik.errors.designation
                                    }
                                    sx={{
                                      backgroundColor: '#FFFFFF',
                                      '& .MuiFilledInput-root': {
                                        backgroundColor: '#FFFFFF',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Name*'
                              name='name'
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.name &&
                                Boolean(formik.errors.name)
                              }
                              helperText={
                                formik.touched.name && formik.errors.name
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('name', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label="Father/Husband's Name"
                              name='fatherHusbandName'
                              value={formik.values.fatherHusbandName}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.fatherHusbandName &&
                                Boolean(formik.errors.fatherHusbandName)
                              }
                              helperText={
                                formik.touched.fatherHusbandName &&
                                formik.errors.fatherHusbandName
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue(
                                        'fatherHusbandName',
                                        ''
                                      )
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Occupation'
                              name='occupation'
                              value={formik.values.occupation}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.occupation &&
                                Boolean(formik.errors.occupation)
                              }
                              helperText={
                                formik.touched.occupation &&
                                formik.errors.occupation
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('occupation', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          {/* add date of birth  */}

                          <Grid item xs={4}>
                            <DatePicker
                              label='Date of Birth'
                              inputFormat='MM/DD/YYYY'
                              backgroundColor='red'
                              value={
                                formik.values.dateOfBirth
                                  ? dayjs(formik.values.dateOfBirth)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'dateOfBirth',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  error={
                                    formik.touched.dateOfBirth &&
                                    Boolean(formik.errors.dateOfBirth)
                                  }
                                  helperText={
                                    formik.touched.dateOfBirth &&
                                    formik.errors.dateOfBirth
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'dateOfBirth',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),

                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={4}>
                            <DatePicker
                              label='Wedding Anniversary'
                              inputFormat='MM/DD/YYYY'
                              value={
                                formik.values.weddingAnniversary
                                  ? dayjs(formik.values.weddingAnniversary)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'weddingAnniversary',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant='filled'
                                  error={
                                    formik.touched.weddingAnniversary &&
                                    Boolean(formik.errors.weddingAnniversary)
                                  }
                                  helperText={
                                    formik.touched.weddingAnniversary &&
                                    formik.errors.weddingAnniversary
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'weddingAnniversary',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>

                          {/* add - date of join  */}
                          <Grid item xs={4}>
                            <DatePicker
                              label='Date of Join'
                              inputFormat='MM/DD/YYYY'
                              value={
                                formik.values.dateOfJoin
                                  ? dayjs(formik.values.dateOfJoin)
                                  : null
                              }
                              onChange={(value) =>
                                formik.setFieldValue(
                                  'dateOfJoin',
                                  value ? value.format('MM/DD/YYYY') : ''
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  variant='filled'
                                  error={
                                    formik.touched.dateOfJoin &&
                                    Boolean(formik.errors.dateOfJoin)
                                  }
                                  helperText={
                                    formik.touched.dateOfJoin &&
                                    formik.errors.dateOfJoin
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue('dateOfJoin', '')
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>
                          {/* Bank Account Number */}
                          <Grid item xs={4}>
                            <InputMask
                              mask='999999999999999999'
                              value={formik.values.bankaccountnumber}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              alwaysShowMask={false}>
                              {(inputProps) => (
                                <TextField
                                  {...inputProps}
                                  variant='filled'
                                  label='Bank Account Number'
                                  name='bankaccountnumber'
                                  onChange={inputProps.handleChange}
                                  onBlur={inputProps.handleBlur}
                                  error={
                                    formik.touched.bankaccountnumber &&
                                    Boolean(formik.errors.bankaccountnumber)
                                  }
                                  helperText={
                                    formik.touched.bankaccountnumber &&
                                    formik.errors.bankaccountnumber
                                  }
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue(
                                            'bankaccountnumber',
                                            ''
                                          )
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 2 }}
                                />
                              )}
                            </InputMask>
                          </Grid>

                          {/* Contact Details Section */}
                          <Grid item xs={12}>
                            <Typography
                              variant='subtitle1'
                              color='#081332'
                              sx={{
                                fontFamily: 'Work Sans',
                                fontSize: '18px',
                                fontWeight: 500,
                                lineHeight: '23.46px',
                              }}>
                              Contact Details
                            </Typography>
                          </Grid>
                          {/* Email Field */}
                          <Grid item xs={4}>
                            <TextField
                              variant='filled'
                              label='Email'
                              name='email'
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                              }
                              helperText={
                                formik.touched.email && formik.errors.email
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('email', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          {/* Phone Number 1 Field */}
                          <Grid item xs={4}>
                            <InputMask
                              mask='9999999999' // 9-digit mask for the bank account number
                              value={formik.values.phone1}
                              onChange={(e) => {
                                formik.setFieldValue('phone1', e.target.value)
                              }}
                              onBlur={formik.handleBlur}
                              alwaysShowMask={false}>
                              {(inputProps) => (
                                <TextField
                                  variant='filled'
                                  label='Phone Number 1*'
                                  name='phone1'
                                  value={formik.values.phone1}
                                  onBlur={inputProps.handleBlur}
                                  error={
                                    formik.touched.phone1 &&
                                    Boolean(formik.errors.phone1)
                                  }
                                  helperText={
                                    formik.touched.phone1 &&
                                    formik.errors.phone1
                                  }
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() =>
                                          formik.setFieldValue('phone1', '')
                                        }>
                                        <HighlightOffIcon
                                          sx={{ color: '#49454F' }}
                                        />
                                      </IconButton>
                                    ),
                                    sx: { backgroundColor: '#FFFFFF' },
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ mb: 1 }}
                                />
                              )}
                            </InputMask>
                          </Grid>
                          {/* Address for Communication Field */}
                          <Grid item xs={4}>
                            <Box sx={{ position: 'relative' }}>
                              {' '}
                              <TextField
                                variant='filled'
                                label='Address for Communication*'
                                name='address'
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.address &&
                                  Boolean(formik.errors.address)
                                }
                                helperText={
                                  formik.touched.address &&
                                  formik.errors.address
                                }
                                fullWidth
                                multiline
                                rows={5}
                                InputProps={{
                                  endAdornment: (
                                    <IconButton
                                      onClick={() =>
                                        formik.setFieldValue('address', '')
                                      }>
                                      <HighlightOffIcon
                                        sx={{ color: '#49454F' }}
                                      />
                                    </IconButton>
                                  ),
                                  sx: { backgroundColor: '#FFFFFF' },
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 1 }}
                              />
                            </Box>
                          </Grid>
                          {/* Phone Number 2 Field */}
                          <Grid
                            item
                            xs={4}
                            sx={{
                              position: 'relative',
                              top: '-5rem',
                            }}>
                            <TextField
                              variant='filled'
                              label='Phone Number 2'
                              name='phone2'
                              value={formik.values.phone2}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.phone2 &&
                                Boolean(formik.errors.phone2)
                              }
                              helperText={
                                formik.touched.phone2 && formik.errors.phone2
                              }
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() =>
                                      formik.setFieldValue('phone2', '')
                                    }>
                                    <HighlightOffIcon
                                      sx={{ color: '#49454F' }}
                                    />
                                  </IconButton>
                                ),
                                sx: { backgroundColor: '#FFFFFF' },
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                          {/* Identity Details Section */}
                          <Grid container spacing={2}>
                            <Grid
                              item
                              xs={12}
                              sx={{ marginLeft: '20px', marginTop: '-2rem' }}>
                              <Typography
                                variant='subtitle1'
                                color='#081332'
                                sx={{
                                  fontFamily: 'Work Sans',
                                  fontSize: '18px',
                                  fontWeight: 500,
                                  lineHeight: '23.46px',
                                }}>
                                Identity Details
                              </Typography>
                            </Grid>

                            <Grid item xs={7.2} sx={{ marginLeft: '20px' }}>
                              <Grid container spacing={2}>
                                {/* pan number details - add */}
                                <Grid item xs={5.5}>
                                  <TextField
                                    variant='filled'
                                    label='PAN Number'
                                    name='pan'
                                    value={formik.values.pan.toUpperCase()}
                                    onChange={(e) => {
                                      let value = e.target.value.toUpperCase()
                                      value = value
                                        .slice(0, 10)
                                        .split('')
                                        .map((char, index) => {
                                          if (index < 5)
                                            return char.replace(/[^A-Z]/g, '')
                                          if (index >= 5 && index < 9)
                                            return char.replace(/[^0-9]/g, '')
                                          if (index === 9)
                                            return char.replace(/[^A-Z]/g, '')
                                          return ''
                                        })
                                        .join('')

                                      formik.setFieldValue('pan', value)
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={
                                      formik.touched.pan &&
                                      Boolean(formik.errors.pan)
                                    }
                                    helperText={
                                      formik.touched.pan && formik.errors.pan
                                    }
                                    InputProps={{
                                      endAdornment: (
                                        <IconButton
                                          onClick={() =>
                                            formik.setFieldValue('pan', '')
                                          }>
                                          <HighlightOffIcon
                                            sx={{ color: '#49454F' }}
                                          />
                                        </IconButton>
                                      ),
                                      sx: { backgroundColor: '#FFFFFF' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: '99%' }}
                                    inputProps={{
                                      maxLength: 10,
                                      style: { textTransform: 'uppercase' },
                                    }}
                                  />
                                </Grid>

                                {/* aadhar number -add  */}
                                <Grid item xs={5.5}>
                                  <InputMask
                                    mask='999999999999' // 9-digit mask for the bank account number
                                    value={formik.values.aadhar}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    alwaysShowMask={false}>
                                    {(inputProps) => (
                                      <TextField
                                        variant='filled'
                                        label='AADHAAR Number*'
                                        name='aadhar'
                                        value={formik.values.aadhar}
                                        onChange={inputProps.handleChange}
                                        onBlur={inputProps.handleBlur}
                                        error={
                                          formik.touched.aadhar &&
                                          Boolean(formik.errors.aadhar)
                                        }
                                        helperText={
                                          formik.touched.aadhar &&
                                          formik.errors.aadhar
                                        }
                                        InputProps={{
                                          endAdornment: (
                                            <IconButton
                                              onClick={() =>
                                                formik.setFieldValue(
                                                  'aadhar',
                                                  ''
                                                )
                                              }>
                                              <HighlightOffIcon
                                                sx={{ color: '#49454F' }}
                                              />
                                            </IconButton>
                                          ),
                                          sx: { backgroundColor: '#FFFFFF' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: '92%' }}
                                      />
                                    )}
                                  </InputMask>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/*Add Associate - Enclosures Section */}
                            <Grid
                              item
                              xs={4.4}
                              sx={{
                                position: 'relative',
                                top: '-2rem',
                                left: '-1rem',
                              }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}>
                                <Typography
                                  variant='subtitle1'
                                  color='#081332'
                                  sx={{
                                    fontFamily: 'Work Sans',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    lineHeight: '23.46px',
                                  }}>
                                  Enclosures
                                </Typography>

                                {/* Residential Proof */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    Residential Proof
                                  </Typography>

                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById(
                                          'residentialProofUpload'
                                        )
                                        .click()
                                    }>
                                    <AttachmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {formik.values.residentialProof && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '4vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.residentialProof.name}
                                    </Typography>
                                  )}
                                  <input
                                    type='file'
                                    id='residentialProofUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'residentialProof')
                                    }
                                  />
                                </Box>

                                {/* PAN Card */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    PAN Card
                                  </Typography>

                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById('panCardUpload')
                                        .click()
                                    }>
                                    <AttachmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {formik.values.panCard && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '7.4vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.panCard.name}
                                    </Typography>
                                  )}
                                  <input
                                    type='file'
                                    id='panCardUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'panCard')
                                    }
                                  />
                                </Box>

                                {/* Aadhaar Card */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}>
                                  <Typography variant='body2'>
                                    Aadhaar Card
                                  </Typography>

                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      marginLeft: '8.5vw',
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById('aadharCardUpload')
                                        .click()
                                    }>
                                    <AttachmentIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {formik.values.aadharCard && (
                                    <Typography
                                      variant='body2'
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        marginLeft: '5.6vw',
                                        textOverflow: 'ellipsis',
                                      }}>
                                      {formik.values.aadharCard.name}
                                    </Typography>
                                  )}
                                  <input
                                    type='file'
                                    id='aadharCardUpload'
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileUpload(e, 'aadharCard')
                                    }
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <DialogActions
                          sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            sx={{
                              borderRadius: '20px',
                              width: '112px',
                              color: 'white',
                            }}
                            disabled={
                              !formik.values.designation ||
                              !formik.values.name ||
                              !formik.values.sponsoredBy ||
                              !formik.values.phone1 ||
                              !formik.values.address ||
                              !formik.values.aadhar ||
                              formik.isSubmitting ||
                              !!formik.errors.designation ||
                              !!formik.errors.name ||
                              !!formik.errors.sponsoredBy ||
                              !!formik.errors.phone1 ||
                              !!formik.errors.address ||
                              !!formik.errors.aadhar
                            }>
                            Save
                          </Button>
                          <Button
                            onClick={handleClose}
                            variant='outlined'
                            color='secondary'
                            sx={{
                              backgroundColor: '#C5C5C5',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: '#A5A5A5',
                              },
                              ml: 2,
                            }}>
                            Cancel
                          </Button>
                        </DialogActions>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </TableContainer>
            </Box>

            {/* pagination Box */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 2,
                width: '100%',
              }}>
              <Box>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 'bold', color: '#000000' }}>
                  {`${
                    filteredAssociates.length === 0 ? 0 : startIndex + 1
                  } - ${Math.min(endIndex, filteredAssociates.length)} of ${
                    filteredAssociates.length
                  } records`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{ color: currentPage === 1 ? '#FFA88D80' : '#F6764E' }}
                  onClick={handleSkipToFirst}
                  disabled={currentPage === 1}>
                  <SkipPreviousIcon />
                </IconButton>

                <IconButton
                  sx={{ color: currentPage === 1 ? '#FFA88D80' : '#F6764E' }}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}>
                  <ChevronLeftIcon />
                </IconButton>

                <IconButton
                  sx={{ color: '#F6764E' }}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}>
                  <ChevronRightIcon />
                </IconButton>

                <IconButton
                  sx={{ color: '#F6764E' }}
                  onClick={handleSkipToLast}
                  disabled={currentPage === totalPages}>
                  <SkipNextIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Confirmation
            open={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleConfirmDelete}
            message={'Are you sure? Do you want to delete?'}
          />
        </Box>
      </Box>

      {/* snackbar  */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}>
          {snackbarMessage}
          <LinearProgress
            variant='determinate'
            value={100}
            sx={{
              animation: 'progressBar 4s linear forwards',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              boxSizing: 'border-box',
              transformOrigin: 'right',
            }}
          />
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes progressBar {
            0% { transform: scaleX(1); }
            100% { transform: scaleX(0); }
          }
        `}
      </style>

      {/* Footer */}
      <Box component='footer' sx={{ height: '10px' }}>
        <Footer />
      </Box>
    </>
  )
}

export default AssociateScreen
